"use client";

import { Loader2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  type DamageComponent,
  DamageComponentSchema,
} from "@/lib/core/schemas/damage-types";
import type {
  PostRollFeedbackDecision,
  PostRollFeedbackEnvelope,
} from "@/lib/core/schemas/post-roll-feedback.schema";
import logger from "@/lib/logger";
import type { PendingActionView } from "@/lib/systems/actions/read-view";
import { cn } from "@/lib/utils";
import { callMcpTool } from "@/src/api/mcp";
import { useGameState } from "./game-state-context";
import {
  PostRollDamageFeedbackOffer,
  PostRollFeedbackOffer,
} from "./post-roll-feedback-offer";
import { DEFAULT_MODIFIERS } from "./skill-check-modifiers";

// ── Types ─────────────────────────────────────────────────────────────

type ActionRollCardProps = {
  action: PendingActionView;
  campaignId: string;
  onResolved?: () => void;
};

type RollOutcome = "hit" | "miss" | "crit" | "fumble" | "pass" | "fail";

type LocalRollResult = {
  step: "attack" | "save";
  roll: { total: number; rolls: number[]; modifierTotal: number };
  outcome: string;
};

type LocalRevealResult = {
  damageComponents: DamageComponent[];
  outcomeId: string | null;
};

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Pure predicate: should handleRollToHit fire the nudge based on the ACK?
 *
 * Miss path (ACK has no awaitingPlayer) → fire the nudge; the narrator
 * should wake up and tell the miss.
 *
 * Hit path (ACK has awaitingPlayer { reason: "damage_reveal" }) → do NOT
 * fire the nudge; the damage reveal click will fire it when the full
 * attack sequence is committed.
 */
export function shouldFireNudgeAfterRoll(ack: {
  awaitingPlayer?: { reason: string };
}): boolean {
  return ack.awaitingPlayer === undefined;
}

function getOutcomeLabel(outcome: string): string {
  const labels: Record<string, string> = {
    hit: "Hit!",
    miss: "Miss",
    crit: "Crit!",
    fumble: "Fumble",
    pass: "Saved!",
    fail: "Failed",
  };
  return labels[outcome] ?? outcome.charAt(0).toUpperCase() + outcome.slice(1);
}

function getOutcomeBadgeClass(outcome: string): string {
  switch (outcome as RollOutcome) {
    case "hit":
      return "bg-green-500/20 text-green-400";
    case "miss":
      return "bg-zinc-500/20 text-zinc-400";
    case "crit":
      return "bg-yellow-400/20 text-yellow-300";
    case "fumble":
      return "bg-red-700/20 text-red-500";
    case "pass":
      return "bg-green-500/20 text-green-400";
    case "fail":
      return "bg-red-500/20 text-red-400";
    default:
      return "bg-zinc-500/20 text-zinc-400";
  }
}

function getOutcomeBorderClass(outcome: string): string {
  switch (outcome as RollOutcome) {
    case "hit":
      return "border-green-500";
    case "miss":
      return "border-zinc-500";
    case "crit":
      return "border-yellow-400";
    case "fumble":
      return "border-red-700";
    case "pass":
      return "border-green-500";
    case "fail":
      return "border-red-500";
    default:
      return "border-zinc-500";
  }
}

/**
 * Identifies the PC target for the action. Reads `pcTargetId` which the
 * kernel sets on the `awaiting_roll` transition and preserves through
 * commit. Falls back to `awaitingTargetId` for rows written before
 * `pcTargetId` existed (legacy / already-in-flight awaiting_roll actions
 * at deploy time).
 */
function findPcTargetId(action: PendingActionView): string | null {
  return action.data.pcTargetId ?? action.data.awaitingTargetId ?? null;
}

function getStepFromAction(
  action: PendingActionView
): "attack" | "save" | "check" {
  // Prefer the persisted rollStep field set during executePendingAction.
  if (action.data.rollStep) {
    return action.data.rollStep;
  }
  // Fall back to reading step from a resolved outcome (e.g. after page refresh
  // where rollStep wasn't stored by an older version of the code).
  const outcomes = action.data.resolvedTargetOutcomes;
  if (outcomes) {
    for (const key of Object.keys(outcomes)) {
      const rollResult = outcomes[key]?.rollResult;
      if (rollResult) {
        return rollResult.step;
      }
    }
  }
  // Default to attack
  return "attack";
}

function getThreshold(
  action: PendingActionView,
  pcId: string | null
): number | null {
  if (!pcId) {
    return null;
  }
  const targetEntry = action.data.targets.find((t) => t.entityId === pcId);
  if (!targetEntry) {
    return null;
  }
  // Look for a "gte" inspector (attack vs AC) — the value is the threshold.
  const inspector = targetEntry.plan.inspectors.find(
    (i) => i.type === "comparison" && i.operator === "gte"
  );
  if (inspector && inspector.type === "comparison") {
    return inspector.value;
  }
  return null;
}

function getRollFormula(
  action: PendingActionView,
  pcId: string | null
): string | null {
  if (!pcId) {
    return null;
  }
  const targetEntry = action.data.targets.find((t) => t.entityId === pcId);
  if (!targetEntry) {
    return null;
  }
  return targetEntry.plan.evaluator.formula ?? null;
}

function getDamageComponents(
  action: PendingActionView,
  pcId: string | null
): DamageComponent[] {
  if (!pcId) {
    return [];
  }
  const targetEntry = action.data.targets.find((t) => t.entityId === pcId);
  if (!targetEntry) {
    return [];
  }
  const outcomes = action.data.resolvedTargetOutcomes;
  if (!outcomes) {
    return [];
  }
  const pcOutcome = outcomes[pcId];
  if (!pcOutcome) {
    return [];
  }
  const matchedId = pcOutcome.matchedOutcomeId;
  return targetEntry.plan.damageComponentsByOutcome?.[matchedId] ?? [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseRevealResult(data: unknown): LocalRevealResult | null {
  if (!isRecord(data)) {
    return null;
  }
  const damageComponentsResult = DamageComponentSchema.array().safeParse(
    data.damageComponents
  );
  if (!damageComponentsResult.success) {
    return null;
  }
  return {
    damageComponents: damageComponentsResult.data,
    outcomeId: typeof data.outcomeId === "string" ? data.outcomeId : null,
  };
}

// ── Sub-components ────────────────────────────────────────────────────

type AttackResolvedCardProps = {
  action: PendingActionView;
  localResult: LocalRollResult;
};

function AttackResolvedCard({ action, localResult }: AttackResolvedCardProps) {
  const step = localResult.step;
  const stepLabel = step === "attack" ? "Attack Roll" : "Saving Throw";
  const outcome = localResult.outcome;
  const threshold = getThreshold(action, findPcTargetId(action));
  const thresholdLabel = step === "attack" ? "AC" : "DC";

  const rawRoll = localResult.roll.total - localResult.roll.modifierTotal;
  const modSign =
    localResult.roll.modifierTotal >= 0
      ? `+${localResult.roll.modifierTotal}`
      : `${localResult.roll.modifierTotal}`;

  const borderClass = getOutcomeBorderClass(outcome);
  const badgeClass = getOutcomeBadgeClass(outcome);

  return (
    <div className={cn("my-2 rounded-lg border bg-card p-4", borderClass)}>
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        {stepLabel}
      </div>
      <div className="font-bold text-4xl text-card-foreground tabular-nums">
        {localResult.roll.total}
      </div>
      <div className="mt-1 text-muted-foreground text-sm">
        {rawRoll} {modSign} modifier
        {threshold !== null ? ` · vs ${thresholdLabel} ${threshold}` : ""}
      </div>
      <div
        className={cn(
          "mt-3 inline-block rounded-full px-3 py-1 font-medium text-xs",
          badgeClass
        )}
      >
        {getOutcomeLabel(outcome)}
      </div>
    </div>
  );
}

type DamagePendingCardProps = {
  damageComponents: DamageComponent[];
  rolling: boolean;
  rollError: string | null;
  onReveal: () => void;
};

function DamagePendingCard({
  damageComponents,
  rolling,
  rollError,
  onReveal,
}: DamagePendingCardProps) {
  return (
    <div className="my-2 rounded-lg border border-red-900/50 bg-card p-4">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        Damage
      </div>

      {damageComponents.length > 0 && (
        <div className="mb-3 flex flex-col gap-1.5">
          {damageComponents.map((component, i) => (
            <div
              className="text-muted-foreground text-sm"
              key={`${component.type}-${i}`}
            >
              <span className="text-card-foreground">{component.formula}</span>{" "}
              <span className="capitalize">{component.type}</span>
            </div>
          ))}
        </div>
      )}

      {rollError && (
        <div className="mb-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive text-xs">
          {rollError}
        </div>
      )}

      <button
        className={cn(
          "rounded px-4 py-1.5 font-medium text-sm disabled:pointer-events-none disabled:opacity-50",
          "bg-red-700 text-white hover:bg-red-600"
        )}
        disabled={rolling}
        onClick={onReveal}
        type="button"
      >
        {rolling ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Rolling...
          </span>
        ) : (
          "Roll Damage"
        )}
      </button>
    </div>
  );
}

type DamageResolvedCardProps = {
  action: PendingActionView;
  localReveal: LocalRevealResult;
};

function DamageResolvedCard({ localReveal }: DamageResolvedCardProps) {
  const { damageComponents } = localReveal;
  const total = damageComponents.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="my-2 rounded-lg border border-red-700 bg-card p-4">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        Damage
      </div>
      <div className="flex flex-col gap-1.5">
        {damageComponents.map((component, i) => (
          <div
            className="flex items-center justify-between text-sm"
            key={`${component.type}-${i}`}
          >
            <span className="text-card-foreground">
              {component.formula}{" "}
              <span className="text-muted-foreground text-xs capitalize">
                {component.type}
              </span>
            </span>
            <span className="font-bold text-red-400 tabular-nums">
              {component.amount}
            </span>
          </div>
        ))}
      </div>
      {damageComponents.length > 1 && (
        <div className="mt-3 flex items-center justify-between border-border border-t pt-2">
          <span className="text-muted-foreground text-xs uppercase tracking-wider">
            Total
          </span>
          <span className="font-bold text-2xl text-red-400 tabular-nums">
            {total}
          </span>
        </div>
      )}
      {damageComponents.length === 1 && (
        <div className="mt-2 font-bold text-2xl text-red-400 tabular-nums">
          {total}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────

export function ActionRollCard({
  action,
  campaignId,
  onResolved,
}: ActionRollCardProps) {
  const [rolling, setRolling] = useState(false);
  const [rollError, setRollError] = useState<string | null>(null);
  const [localResult, setLocalResult] = useState<LocalRollResult | null>(null);
  const [localReveal, setLocalReveal] = useState<LocalRevealResult | null>(
    null
  );
  const [pendingPostRollFeedback, setPendingPostRollFeedback] = useState<
    PostRollFeedbackEnvelope | null | undefined
  >(undefined);
  const [reactionDeciding, setReactionDeciding] = useState(false);
  const [reactionError, setReactionError] = useState<string | null>(null);

  // PC target is persisted explicitly on the action by the kernel
  // (see controller.ts transition) and survives commit — no scan needed.
  // awaitingTargetId is the pre-pcTargetId fallback for rows written
  // before this field existed.
  const { awaitingTargetId, pcTargetId: storedPcTargetId } = action.data;
  const pcId = storedPcTargetId ?? awaitingTargetId ?? null;

  // On mount (or when action prop changes), hydrate localResult from
  // persisted resolvedTargetOutcomes so page refresh — or post-commit
  // historical rendering — doesn't lose the resolved attack card state.
  const persistedResult = useMemo<LocalRollResult | null>(() => {
    if (!pcId) {
      return null;
    }
    const outcome = action.data.resolvedTargetOutcomes?.[pcId];
    if (!outcome?.rollResult) {
      return null;
    }
    return outcome.rollResult as LocalRollResult;
  }, [pcId, action.data.resolvedTargetOutcomes]);

  // Use the persisted result as the effective display result when no local
  // (in-memory) result has been set yet.
  const displayResult = localResult ?? persistedResult;
  const activePostRollFeedback =
    pendingPostRollFeedback !== undefined
      ? pendingPostRollFeedback
      : (action.data.pendingPostRollFeedback ?? null);
  const activeDamageFeedback = action.data.pendingDamageFeedback ?? null;

  const step = getStepFromAction(action);
  const isAwaitingRoll = action.status === "awaiting_roll";
  const isAwaitingReveal = action.status === "awaiting_reveal";
  const isAwaitingReaction = action.status === "awaiting_reaction";

  const threshold = getThreshold(action, pcId);
  const formula = getRollFormula(action, pcId);
  const damageComponents = getDamageComponents(action, pcId);

  const thresholdLabel = step === "attack" ? "Target AC" : "DC";
  const headerLabel = step === "attack" ? "Attack Roll" : "Saving Throw";
  const rollButtonLabel = step === "attack" ? "Roll to Hit" : "Roll Save";
  const rollButtonClass =
    step === "attack"
      ? "bg-blue-600 hover:bg-blue-500 text-white"
      : "bg-amber-600 hover:bg-amber-500 text-white";

  const { invalidate } = useGameState();

  const handleRollToHit = useCallback(async () => {
    setRolling(true);
    setRollError(null);
    try {
      const ack = await callMcpTool(
        "resolve_action_roll",
        {
          actionId: action.entityId,
          playerModifiers: DEFAULT_MODIFIERS,
        },
        campaignId
      );
      // Extract roll result from ACK data
      if (
        ack.data &&
        typeof ack.data === "object" &&
        "postRollFeedback" in (ack.data as Record<string, unknown>)
      ) {
        const data = ack.data as Record<string, unknown>;
        setPendingPostRollFeedback(
          data.postRollFeedback as PostRollFeedbackEnvelope
        );
        invalidate(["pending-actions", "effect-cards"]);
        return;
      }
      if (
        ack.data &&
        typeof ack.data === "object" &&
        "rollResult" in (ack.data as Record<string, unknown>)
      ) {
        const data = ack.data as Record<string, unknown>;
        setPendingPostRollFeedback(null);
        setLocalResult(data.rollResult as LocalRollResult);
      }
      // Re-hydrate pending-actions so the card sees the new status.
      // This is direct-to-kernel, so the agent's tool_complete SSE
      // invalidation path doesn't fire — we have to do it ourselves.
      invalidate(["pending-actions", "effect-cards"]);
      if (shouldFireNudgeAfterRoll(ack)) {
        onResolved?.();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to resolve roll";
      logger.error(
        { err, actionId: action.entityId },
        "Failed to resolve action roll"
      );
      setRollError(message);
    } finally {
      setRolling(false);
    }
  }, [action.entityId, campaignId, onResolved, invalidate]);

  const handlePostRollFeedback = useCallback(
    async (decision: PostRollFeedbackDecision) => {
      setRolling(true);
      setRollError(null);
      try {
        const ack = await callMcpTool(
          "resolve_action_roll",
          {
            actionId: action.entityId,
            playerModifiers: DEFAULT_MODIFIERS,
            postRollFeedback: decision,
          },
          campaignId
        );
        setPendingPostRollFeedback(null);
        if (
          ack.data &&
          typeof ack.data === "object" &&
          "rollResult" in (ack.data as Record<string, unknown>)
        ) {
          const data = ack.data as Record<string, unknown>;
          setLocalResult(data.rollResult as LocalRollResult);
        }
        invalidate(["pending-actions", "effect-cards"]);
        if (shouldFireNudgeAfterRoll(ack)) {
          onResolved?.();
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to resolve roll";
        logger.error(
          { err, actionId: action.entityId },
          "Failed to resolve post-roll feedback"
        );
        setRollError(message);
      } finally {
        setRolling(false);
      }
    },
    [action.entityId, campaignId, onResolved, invalidate]
  );

  const handleRevealDamage = useCallback(async () => {
    setRolling(true);
    setRollError(null);
    try {
      const ack = await callMcpTool(
        "reveal_action_effect",
        { actionId: action.entityId },
        campaignId
      );
      const revealResult = parseRevealResult(ack.data);
      if (revealResult) {
        setLocalReveal(revealResult);
      }
      // Re-hydrate pending-actions + effect-cards so the card unmounts
      // (status → committed) and any newly-committed effect cards render.
      invalidate(["pending-actions", "effect-cards"]);
      onResolved?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to reveal damage";
      logger.error(
        { err, actionId: action.entityId },
        "Failed to reveal action effect"
      );
      setRollError(message);
    } finally {
      setRolling(false);
    }
  }, [action.entityId, campaignId, onResolved, invalidate]);

  const handleDamageFeedback = useCallback(
    async (decision: PostRollFeedbackDecision) => {
      setRolling(true);
      setRollError(null);
      try {
        const ack = await callMcpTool(
          "reveal_action_effect",
          { actionId: action.entityId, damageFeedback: decision },
          campaignId
        );
        const revealResult = parseRevealResult(ack.data);
        if (revealResult) {
          setLocalReveal(revealResult);
        }
        // Reuse the same post-reveal refresh path as the plain damage reveal.
        invalidate(["pending-actions", "effect-cards"]);
        onResolved?.();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to reveal damage";
        logger.error(
          { err, actionId: action.entityId },
          "Failed to reveal action effect with damage feedback"
        );
        setRollError(message);
      } finally {
        setRolling(false);
      }
    },
    [action.entityId, campaignId, onResolved, invalidate]
  );

  const handleReactionDecision = useCallback(
    async (decision: "take" | "decline") => {
      setReactionDeciding(true);
      setReactionError(null);
      try {
        await callMcpTool(
          "resolve_action_reaction",
          { actionId: action.entityId, decision },
          campaignId
        );
        // Re-hydrate pending-actions + effect-cards; the triggering action
        // transitions to committed and any dispatched effect cards appear.
        invalidate(["pending-actions", "effect-cards"]);
        onResolved?.();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to resolve reaction";
        logger.error(
          { err, actionId: action.entityId, decision },
          "Failed to resolve action reaction"
        );
        setReactionError(message);
      } finally {
        setReactionDeciding(false);
      }
    },
    [action.entityId, campaignId, onResolved, invalidate]
  );

  // ── Resolved: attack/save card already rolled ─────────────────────
  if (activePostRollFeedback) {
    return (
      <PostRollFeedbackOffer
        disabled={rolling}
        envelope={activePostRollFeedback}
        onDecision={handlePostRollFeedback}
      />
    );
  }

  // ── Resolved: attack/save card already rolled ─────────────────────
  if (displayResult) {
    return (
      <>
        <AttackResolvedCard action={action} localResult={displayResult} />
        {/* If the action transitions to awaiting_reveal after our roll, show damage card */}
        {isAwaitingReveal && activeDamageFeedback && !localReveal && (
          <PostRollDamageFeedbackOffer
            disabled={rolling}
            envelope={activeDamageFeedback}
            onDecision={handleDamageFeedback}
          />
        )}
        {isAwaitingReveal && !activeDamageFeedback && !localReveal && (
          <DamagePendingCard
            damageComponents={damageComponents}
            onReveal={handleRevealDamage}
            rollError={rollError}
            rolling={rolling}
          />
        )}
        {localReveal && (
          <DamageResolvedCard action={action} localReveal={localReveal} />
        )}
      </>
    );
  }

  // ── Resolved: damage revealed ─────────────────────────────────────
  if (localReveal) {
    return <DamageResolvedCard action={action} localReveal={localReveal} />;
  }

  // ── Pending: awaiting_reveal (damage card) ────────────────────────
  if (isAwaitingReveal) {
    if (activeDamageFeedback) {
      return (
        <PostRollDamageFeedbackOffer
          disabled={rolling}
          envelope={activeDamageFeedback}
          onDecision={handleDamageFeedback}
        />
      );
    }
    return (
      <DamagePendingCard
        damageComponents={damageComponents}
        onReveal={handleRevealDamage}
        rollError={rollError}
        rolling={rolling}
      />
    );
  }

  // ── Pending: awaiting_reaction (reaction offer card) ─────────────
  if (isAwaitingReaction) {
    // Read the provisional outcome that was stored when the pipeline paused.
    const pausedTargetId = action.data.awaitingTargetId ?? null;
    const provisionalOutcome = pausedTargetId
      ? (action.data.resolvedTargetOutcomes?.[pausedTargetId]?.rollResult
          ?.outcome ?? null)
      : null;
    const outcomeLabel = provisionalOutcome
      ? getOutcomeLabel(provisionalOutcome)
      : null;

    return (
      <div className="my-2 rounded-lg border border-amber-500/50 bg-card p-4">
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          Reaction Available
        </div>
        <p className="mb-1 text-card-foreground text-sm">
          A reaction is available to interrupt this attack
          {outcomeLabel ? ` (provisional: ${outcomeLabel})` : ""}.
        </p>
        <p className="mb-3 text-muted-foreground text-xs">
          Use Shield or another reaction to potentially change the outcome, or
          decline to let the attack resolve as-is.
        </p>

        {reactionError && (
          <div className="mb-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive text-xs">
            {reactionError}
          </div>
        )}

        <div className="flex gap-2">
          <button
            className={cn(
              "rounded px-4 py-1.5 font-medium text-sm disabled:pointer-events-none disabled:opacity-50",
              "bg-amber-600 text-white hover:bg-amber-500"
            )}
            disabled={reactionDeciding}
            onClick={() => handleReactionDecision("take")}
            type="button"
          >
            {reactionDeciding ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Resolving...
              </span>
            ) : (
              "Use Reaction"
            )}
          </button>
          <button
            className={cn(
              "rounded px-4 py-1.5 font-medium text-sm disabled:pointer-events-none disabled:opacity-50",
              "bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
            )}
            disabled={reactionDeciding}
            onClick={() => handleReactionDecision("decline")}
            type="button"
          >
            Decline
          </button>
        </div>
      </div>
    );
  }

  // ── Pending: awaiting_roll (attack/save card) ─────────────────────
  if (!isAwaitingRoll) {
    return null;
  }

  return (
    <div className="my-2 rounded-lg border border-border bg-card p-4">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        {headerLabel}
      </div>

      {/* Threshold badge */}
      {threshold !== null && (
        <div className="mb-2 inline-block rounded border border-border px-2 py-0.5 text-muted-foreground text-xs">
          {thresholdLabel}: {threshold}
        </div>
      )}

      {/* Formula */}
      {formula && (
        <p className="mb-3 text-card-foreground text-sm">
          Roll {formula}
          {step === "attack" ? " to hit" : " to save"}
        </p>
      )}

      {/* Error */}
      {rollError && (
        <div className="mb-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive text-xs">
          {rollError}
        </div>
      )}

      {/* Roll button */}
      <button
        className={cn(
          "rounded px-4 py-1.5 font-medium text-sm disabled:pointer-events-none disabled:opacity-50",
          rollButtonClass
        )}
        disabled={rolling}
        onClick={handleRollToHit}
        type="button"
      >
        {rolling ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Rolling...
          </span>
        ) : (
          rollButtonLabel
        )}
      </button>
    </div>
  );
}
